import { saveAs } from "file-saver";

const getNextCell = (worksheet, anchorCell) => {
  /* this function simply returns the cell immediately to the right 
    (one column over) from the given anchor cell
    */
  const row = worksheet.getRow(anchorCell.row);
  return row.getCell(anchorCell.col + 1);
};
/*
  Named Ranges on template:
            invoiceNumber:  increments by one for every invoice
            invoiceDate:    the first day of the month of the invoice
            billingPeriod:  the pay period (the month prior to the invoice date)
            paymentDate:    3 months after invoice date
            poNumber:       input by admin on front end
            poLineOne:      where the first line item should go
            totalAmount:    total amount charged in invoice
*/

export const createInvoice = async (invoiceData, workbook) => {
  const worksheet = workbook.getWorksheet(1);
  const lines = invoiceData.lines;
  const invoiceDateCell = getNextCell(
    worksheet,
    findCellByValue(worksheet, "Invoice Date"),
  );
  // how many months were between the current invoice date and the template invoice date
  const interval = monthsBetween(
    invoiceDateCell.value,
    invoiceData.invoiceDate,
  );
  // write invoice date
  invoiceDateCell.value = formatDate(invoiceData.invoiceDate);

  // write payment date
  const paymentDateCell = getNextCell(
    worksheet,
    findCellByValue(worksheet, "~Payment Date"),
  );
  paymentDateCell.value = formatDate(invoiceData.paymentDate);

  // write invoice number
  const invoiceNumCell = getNextCell(
    worksheet,
    findCellByValue(worksheet, "Invoice #"),
  );

  let invoiceNumber = invoiceNumCell.value.split("-");
  invoiceNumber[2] = Number(invoiceNumber[2]) + interval;
  invoiceNumber = invoiceNumber.join("-");
  invoiceNumCell.value = invoiceNumber;

  // write billing period
  const billPeriodCell = getNextCell(
    worksheet,
    findCellByValue(worksheet, "Billing Period"),
  );

  billPeriodCell.value = invoiceData.billPeriod;

  // write PO number
  const poCell = getNextCell(
    worksheet,
    findCellByValue(worksheet, "PO Number"),
  );
  poCell.value = invoiceData.poNumber;
  // this is where the grand total should start totaling
  const initialRow = findCellByValue(worksheet, "PO Line Item").row + 1;

  lines.forEach((line, index) => {
    // find the cell with this employees name in it
    const nameCell = findCellByValue(worksheet, line.employeeName);
    if (!nameCell) {
      console.warn("Could not find entry for employee [" + line.employeeName + "]");
      return;
    }
    const row = nameCell.row;
    const col = nameCell.col;
  
    const codeColumn = col + 1;
    const hoursColumn = col + 2;
    const rateColumn = col + 3; // these are used for the formulas
    const totalColumn = col + 4;
    // get letter column of excel file to build total formula
    const hoursLetter = worksheet.getColumn(hoursColumn).letter;
    const rateLetter = worksheet.getColumn(rateColumn).letter;

    console.log(worksheet.getCell(row, codeColumn).value);

    const rate = parseFloat(worksheet.getCell(row, rateColumn).value);
    worksheet.getCell(row, hoursColumn).value = line.hours;
    worksheet.getCell(row, totalColumn).value = {
      formula: `${hoursLetter}${row}*${rateLetter}${row}`,
      result: line.hours * (rate || 0), // calculate result so user sees it immediately
    }; // insert totals formula

    if (line.overtimeHours > 0) {
      const overtimeCode = worksheet.getCell(row, codeColumn).value + "X";
      console.log("OT CODE " + overtimeCode)
      const overtimeRow = findCellByValue(worksheet, overtimeCode).row;
      const overtimeRate = parseFloat(
        worksheet.getCell(overtimeRow, rateColumn).value,
      );
      worksheet.getCell(overtimeRow, hoursColumn).value = line.overtimeHours;
      worksheet.getCell(overtimeRow, totalColumn).value = {
        formula: `${hoursLetter}${overtimeRow}*${rateLetter}${overtimeRow}`,
        result: line.overtimeHours * (overtimeRate || 0), // calculate result so user sees it immediately
      };
    }
  });

  // fill out total
  const totalCell = getNextCell(
    worksheet,
    findCellByValue(worksheet, "Total:"),
  );
  const totalLetter = worksheet.getColumn(totalCell.col).letter;
  totalCell.value = {
    formula: `SUM(${totalLetter}${initialRow}:${totalLetter}${totalCell.row - 1})`,
  };

  try {
    const fileName =
      invoiceData.contractName +
      " Invoice " +
      new Date(invoiceData.invoiceDate + "T00:00:00").toLocaleString(
        "default",
        {
          month: "long",
          year: "numeric",
        },
      );
    generateFile(workbook, fileName);
  } catch (error) {
    console.log("Error generating invoice" + error);
  }
};

const generateFile = async (workbook, fileName) => {
  // Generate the Binary Buffer
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  // Trigger the Download
  saveAs(blob, fileName);
};

/* 
    Returns the cell location of a given named range

const getNamedRange = (name, workbook) => {
  //extract all names from the file and attempt to match it with given name
  const allNames = workbook.definedNames.model;
  const nameMatch = allNames.find(
    (n) => n.name.toLowerCase() === name.toLowerCase(),
  );
  if (nameMatch) {
    const range = nameMatch.ranges[0]; // e.g., "Sheet1!$B$5"

    // Parse the Sheet and Address
    const [sheetPart, addressPart] = range.includes("!")
      ? range.split("!")
      : [null, range];

    // Clean up the sheet name (remove quotes if sheet has spaces)
    const sheetName = sheetPart ? sheetPart.replace(/'/g, "") : null;

    // Clean up the address (remove $ and handle ranges like A1:B2 by taking top-left)
    const cleanAddress =
      addressPart.replace(/\$/g, "").split(":")[1] ||
      addressPart.replace(/\$/g, "").split(":")[0];
    const targetCell = addressPart.replace(/\$/g, "").split(":")[0];

    // Return the cell
    const worksheet = sheetName
      ? workbook.getWorksheet(sheetName)
      : workbook.getWorksheet(1);

    return worksheet.getCell(targetCell);
  } else {
    throw Error("Named range not found!");
  }
};
*/

const findCellByValue = (worksheet, searchValue) => {
  if (!worksheet) return null;

  let foundCell = null;
  const searchLower = searchValue.toLowerCase();

  worksheet.eachRow((row) => {
    // 1. If we already found it in a previous row,
    // skip this row entirely.
    if (foundCell) return;

    row.eachCell((cell) => {
      // 2. If we already found it in this row,
      // skip any further cells.
      if (foundCell) return;

      const cellValue = cell.value
        ? String(cell.value).trim().toLowerCase()
        : "";

      if (cellValue === searchLower) {
        foundCell = cell;
      }
    });
  });

  return foundCell;
};


// calculate how many months are between 2 dates
const monthsBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return (
    (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth())
  );
};

// this is the only way i can get ExcelJS to write the dates properly in excel
// it's super annoying and very inconvenient but it works
const formatDate = (dateStr) => {
  // Split your Java date: "2026-04-01"
  const [y, m, d] = dateStr.split("-").map(Number);

  // Calculate the Excel Serial Date using UTC to prevent shifting
  // Excel's "Day 0" is Dec 30, 1899.
  const utcDate = Date.UTC(y, m - 1, d);
  const excelBaseDate = Date.UTC(1899, 11, 30);

  // Difference in milliseconds converted to days
  return Math.floor((utcDate - excelBaseDate) / (24 * 60 * 60 * 1000));
};
